"use client";

import { useEffect, useRef, useState } from "react";
import Autoplay from "embla-carousel-autoplay";
import { Card, CardContent } from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import ReviewsSkeletonCard from "@/components/ReviewsSkeletonCard";

type GoogleReview = {
  author_name: string;
  profile_photo_url?: string;
  rating: number;
  text: string;
  relative_time_description: string;
};

function StarRating({ rating }: { rating: number }) {
  return (
    <div
      className="flex gap-0.5 text-yellow-400 text-base"
      aria-label={`Hodnoceni: ${rating} z 5`}
    >
      {Array.from({ length: 5 }).map((_, i) => (
        <span key={i}>{i < rating ? "\u2605" : "\u2606"}</span>
      ))}
    </div>
  );
}

function GoogleLogo() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      aria-label="Google"
    >
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

export default function ReviewsCarousel() {
  const [reviews, setReviews] = useState<GoogleReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const autoplayPlugin = useRef(
    Autoplay({ delay: 5000, stopOnInteraction: false, stopOnMouseEnter: true })
  );

  useEffect(() => {
    fetch("/api/reviews")
      .then((res) => {
        if (!res.ok) throw new Error("Nepodarilo se nacist recenze.");
        return res.json();
      })
      .then((data) => {
        setReviews(data.reviews ?? []);
        setLoading(false);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Nastala chyba.");
        setLoading(false);
      });
  }, []);

  if (error) {
    return (
      <div className="flex justify-center py-8">
        <Card className="max-w-sm w-full rounded-2xl">
          <CardContent className="p-5 text-center text-muted-foreground text-sm">
            {error}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1100px] px-16 relative">
      <h2 className="text-center font-bold text-4xl mb-12">Ověřené recenze z Google</h2>
      <Carousel
        opts={{ align: "start", loop: true }}
        plugins={[autoplayPlugin.current]}
        className="w-full"
      >
        <CarouselContent className="-ml-4">
          {loading
            ? Array.from({ length: 3 }).map((_, i) => (
                <CarouselItem key={i} className="pl-4 basis-full sm:basis-1/2 lg:basis-1/3">
                  <ReviewsSkeletonCard />
                </CarouselItem>
              ))
            : reviews.map((review, i) => (
                <CarouselItem key={i} className="pl-4 basis-full sm:basis-1/2 lg:basis-1/3">
                  <Card className="h-full rounded-2xl shadow-sm border border-border/60 hover:shadow-md transition-shadow duration-200">
                    <CardContent className="p-5 flex flex-col gap-3 h-full">
                      <div className="flex items-center gap-3">
                        {review.profile_photo_url ? (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img
                            src={`/api/proxy-image?url=${encodeURIComponent(review.profile_photo_url)}`}
                            alt={review.author_name}
                            width={40}
                            height={40}
                            className="rounded-full flex-shrink-0 object-cover h-10 w-10"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 text-primary font-semibold text-sm">
                            {review.author_name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-sm leading-tight">{review.author_name}</p>
                          <StarRating rating={review.rating} />
                        </div>
                      </div>

                      <p className="text-sm text-muted-foreground line-clamp-5 flex-1 leading-relaxed">
                        {review.text}
                      </p>

                      <div className="flex items-center justify-between pt-1 border-t border-border/40">
                        <p className="text-xs text-muted-foreground">{review.relative_time_description}</p>
                        <GoogleLogo />
                      </div>
                    </CardContent>
                  </Card>
                </CarouselItem>
              ))}
        </CarouselContent>

        <CarouselPrevious className="absolute -left-12 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/80 border border-border shadow-sm hover:bg-white transition-colors cursor-pointer" />
        <CarouselNext className="absolute -right-12 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/80 border border-border shadow-sm hover:bg-white transition-colors cursor-pointer" />
      </Carousel>
    </div>
  );
}
